import { useMemo, type FormEvent, type ReactElement } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BrowsePatientsTableBody } from '@/features/patient-search/components/BrowsePatientsTableBody';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  DataTable,
  DataTableToolbar,
  type SortDirection,
} from '@/components/ui/data-table';
import { useClientListPagination } from '@/hooks/useClientListPagination';
import type { PatientSortKey } from '@/features/patient-search/lib/sortPatientSummaries';
import type { PatientSummaryViewModel } from '@/models/patient';

export interface BrowsePatientsPanelProps {
  readonly programs: readonly string[];
  readonly gcs: readonly string[];
  readonly q: string;
  readonly onQueryChange: (q: string) => void;
  readonly program: string;
  readonly onProgramChange: (p: string) => void;
  readonly gc: string;
  readonly onGcChange: (g: string) => void;
  readonly patients: readonly PatientSummaryViewModel[];
  readonly isMineRoute: boolean;
  readonly sortKey: PatientSortKey | null;
  readonly sortDir: SortDirection;
  readonly onSortChange: (key: PatientSortKey) => void;
}

export function BrowsePatientsPanel({
  programs,
  gcs,
  q,
  onQueryChange,
  program,
  onProgramChange,
  gc,
  onGcChange,
  patients: list,
  isMineRoute,
  sortKey,
  sortDir,
  onSortChange,
}: BrowsePatientsPanelProps): ReactElement {
  const { t } = useTranslation();
  const filterKey = useMemo(
    () => [q, program, gc, isMineRoute, sortKey ?? '', sortDir].join('\0'),
    [q, program, gc, isMineRoute, sortKey, sortDir],
  );
  const pagination = useClientListPagination({ items: list, filterKey });
  const {
    total,
    pageItems,
    from,
    to,
    onPageSizeChange,
    onPagePrevious,
    onPageNext,
    canGoPrevious,
    canGoNext,
    pageSize,
  } = pagination;

  function onSearchFormSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
  }

  return (
    <div className="space-y-4">
      <form
        onSubmit={onSearchFormSubmit}
        className="flex flex-wrap items-end gap-3"
        role="search"
        aria-label={t('routes.browsePatients.searchAria')}
      >
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-8 h-9 bg-card border-border text-sm"
            placeholder={t('routes.browsePatients.searchPlaceholder')}
            value={q}
            onChange={(e) => {
              onQueryChange(e.target.value);
            }}
            name="q"
            aria-label={t('routes.browsePatients.searchPlaceholder')}
          />
        </div>

        <div className="flex items-end gap-3">
          <div className="space-y-1 min-w-[140px]">
            <Label
              htmlFor="filter-program"
              className="text-xs text-muted-foreground"
            >
              {t('routes.browsePatients.filterProgram')}
            </Label>
            <Select
              id="filter-program"
              value={program}
              onChange={(e) => {
                onProgramChange(e.target.value);
              }}
            >
              <option value="all">
                {t('routes.browsePatients.filterAllPrograms')}
              </option>
              {programs.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1 min-w-[160px]">
            <Label
              htmlFor="filter-gc"
              className="text-xs text-muted-foreground"
            >
              {t('routes.browsePatients.filterGc')}
            </Label>
            <Select
              id="filter-gc"
              value={gc}
              onChange={(e) => {
                onGcChange(e.target.value);
              }}
            >
              <option value="all">
                {t('routes.browsePatients.filterAllGcs')}
              </option>
              {gcs.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </form>

      {total === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
            <Search
              className="h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('routes.browsePatients.empty')}
          </p>
        </div>
      ) : (
        <DataTable aria-label={t('routes.browsePatients.tableRegion')}>
          <DataTableToolbar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            from={from}
            to={to}
            total={total}
            onPagePrevious={onPagePrevious}
            onPageNext={onPageNext}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            pageSizeSelectId="browse-patients-page-size"
          />
          <BrowsePatientsTableBody
            patients={pageItems}
            emptyCell={t('patient.demographics.emptyValue')}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={onSortChange}
          />
        </DataTable>
      )}
    </div>
  );
}
